/**
 * Notify Me API 集成 - 性能和可靠性优化版
 * 用于提交缺货通知订阅请求
 */

// 防止重复声明
if (typeof window.NotifyMeAPI === 'undefined') {

class NotifyMeAPI {
  constructor() {
    this.apiEndpoint = 'https://api.notify-me.app/subscription/';
    this.shopId = this.getShopId();
    this.shopDomain = window.Shopify?.shop || window.location.hostname;

    // 性能优化: 预连接到 API 域名
    this.preconnectToAPI();

    // 错误重试配置
    this.retryConfig = {
      maxRetries: 2,
      retryDelay: 1000, // 1秒
      backoffMultiplier: 2 // 指数退避
    };
  }

  /**
   * 性能优化: 预连接到 API 域名
   */
  preconnectToAPI() {
    if (typeof document === 'undefined') return;

    // 检查是否已经添加了预连接
    const existingLink = document.querySelector('link[rel="preconnect"][href*="notify-me.app"]');
    if (existingLink) return;

    // 添加 DNS 预解析和预连接
    const preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = 'https://api.notify-me.app';
    preconnect.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect);

    // 添加 DNS 预取
    const dnsPrefetch = document.createElement('link');
    dnsPrefetch.rel = 'dns-prefetch';
    dnsPrefetch.href = 'https://api.notify-me.app';
    document.head.appendChild(dnsPrefetch);
  }

  /**
   * 获取 Shop ID
   * 需要在主题设置中配置,或者从某个地方获取
   */
  getShopId() {
    // 方式1: 从主题设置读取
    const configuredShopId = parseInt(window.notifyMeConfig?.shopId, 10);
    if (configuredShopId) {
      return configuredShopId;
    }

    // 方式2: 从页面元素读取
    const shopIdElement = document.querySelector('[data-notify-me-shop-id]');
    if (shopIdElement) {
      return parseInt(shopIdElement.dataset.notifyMeShopId);
    }

    // 默认值 - 需要替换为实际的 Shop ID
    return 32025;
  }

  /**
   * 提交订阅请求(带重试机制)
   * @param {Object} data - 订阅数据
   * @param {number} retryCount - 当前重试次数
   * @returns {Promise}
   */
  async subscribe(data, retryCount = 0) {
    const {
      email,
      phoneNumber = null,
      whatsappNumber = null,
      productId,
      productUrl,
      variantId,
      locale = 'en',
      contactListConsent = true
    } = data;

    // 获取用户时区
    const timezone = this.getUserTimezone();

    // 构建请求体
    const requestBody = {
      email: email,
      phone_number: phoneNumber,
      whatsapp_number: whatsappNumber,
      push_token: null,
      push_provider: null,
      locale: locale,
      product_id: productId,
      url: productUrl,
      variant_id: String(variantId),
      timezone: timezone,
      contact_list_consent: contactListConsent,
      shop_id: this.shopId
    };

    try {
      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: {
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // 如果是网络错误或服务器错误,且未达到最大重试次数,则重试
        if ((response.status >= 500 || response.status === 429) && retryCount < this.retryConfig.maxRetries) {
          const delay = this.retryConfig.retryDelay * Math.pow(this.retryConfig.backoffMultiplier, retryCount);
          console.log(`Notify Me API: 请求失败,${delay}ms 后重试 (${retryCount + 1}/${this.retryConfig.maxRetries})`);

          await this.sleep(delay);
          return this.subscribe(data, retryCount + 1);
        }

        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        success: true,
        data: result
      };

    } catch (error) {
      // 网络错误重试
      if (error.name === 'TypeError' && retryCount < this.retryConfig.maxRetries) {
        const delay = this.retryConfig.retryDelay * Math.pow(this.retryConfig.backoffMultiplier, retryCount);
        console.log(`Notify Me API: 网络错误,${delay}ms 后重试 (${retryCount + 1}/${this.retryConfig.maxRetries})`);

        await this.sleep(delay);
        return this.subscribe(data, retryCount + 1);
      }

      console.error('Notify Me API Error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * 性能优化: 延迟函数
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * 获取用户时区
   * @returns {String} 时区偏移,例如 "+8"
   */
  getUserTimezone() {
    const offset = -new Date().getTimezoneOffset() / 60;
    return offset >= 0 ? `+${offset}` : String(offset);
  }

  /**
   * 从产品数据创建订阅请求
   * @param {Object} params
   * @returns {Promise}
   */
  async subscribeFromProduct(params) {
    const {
      email,
      product,
      variant,
      selectedSize
    } = params;

    // 构建产品 URL
    const productUrl = `${window.location.origin}/products/${product.handle}?variant=${variant.id}`;

    return await this.subscribe({
      email: email,
      productId: product.id,
      productUrl: productUrl,
      variantId: variant.id,
      locale: document.documentElement.lang || 'en'
    });
  }
}

// 导出类到 window
window.NotifyMeAPI = NotifyMeAPI;

// 全局实例（仅在不存在时创建）
if (!window.notifyMeAPI) {
  window.notifyMeAPI = new NotifyMeAPI();
}

} // end if (typeof window.NotifyMeAPI === 'undefined')
